import { Injectable } from '@angular/core';
import {
	DataRepository,
	FlatTableDataRepository,
	SamplesRepository,
	TableRepository,
	WorkspaceDatabaseKeys
} from '@database/workspace';
import { QueryParamsProvider } from '@domain/routing';
import { DataModel, PrimaryKeyValueModel, SurroundingIntervalsModel } from '@shared';
import { combineLatest, Observable, switchMap, map, iif } from 'rxjs';
import { BusinessLayerService, TableTypeProvider } from '.';
import { CollectionCountAppModel } from '@database/central';
import { SamplesTableProvider } from '../samples/samples-table.provider';
import { StandardTableProvider } from '../standards/standard-table.provider';

@Injectable()
export class DataByTableTypeProvider {
	public constructor(
		private tableTypeService: TableTypeProvider,
		private dataRepository: DataRepository,
		private flatTableDataRepository: FlatTableDataRepository,
		private businessLayerService: BusinessLayerService,
		private queryParamsProvider: QueryParamsProvider,
		private tableRepository: TableRepository,
		private sampleRepository: SamplesRepository,
		private samplesTableProvider: SamplesTableProvider,
		private standardTableProvider: StandardTableProvider
	) {}

	public getSurroundingIntervals$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<SurroundingIntervalsModel> {
		return this.tableTypeService
			.isFlatSamplesTable$(workfileId, childTableName ? childTableName : parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.flatTableDataRepository.getSurroundingIntervals$(
							parentTableName.toLowerCase(),
							projectNumber,
							Number(entryId),
							depthFrom,
							depthTo
						),
						
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.dataRepository.getSurroundingIntervals$(
										childTableName.toLowerCase(),
										projectNumber,
										primaryKeyValue,
										Number(childEntryId),
										depthFrom,
										depthTo
									)
								)
							)
					)
				)
			);
	}

	public getOutsideIntervalData$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel[]> {
		return this.tableTypeService
			.isFlatSamplesTable$(workfileId, childTableName ? childTableName : parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.flatTableDataRepository.getOutsideIntervalData$(
							parentTableName.toLowerCase(),
							projectNumber,
							Number(childEntryId),
							depthFrom,
							depthTo
						),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.dataRepository.getOutsideIntervalData$(
										childTableName.toLowerCase(),
										projectNumber,
										primaryKeyValue,
										Number(childEntryId),
										depthFrom,
										depthTo
									)
								)
							)
					)
				)
			);
	}

	public getParentSampleNumberLinkedData$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: string,
		childEntryId: string,
		parentSampleNumber: string
	): Observable<DataModel[]> {
		return this.tableTypeService
			.isFlatSamplesTable$(workfileId, childTableName ? childTableName : parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.sampleRepository.getDuplicateSampleByParentNumber$(
							parentTableName,
							projectNumber,
							parentSampleNumber,
							[Number(childEntryId)]
						),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.sampleRepository.getDuplicateSampleByParentNumber$(
										childTableName,
										projectNumber,
										parentSampleNumber,
										[Number(childEntryId)],
										primaryKeyValue
									)
								)
							)
					)
				)
			);
	}

	public getOverlappingData$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: string,
		childEntryId: string,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel> {
		return this.tableTypeService
			.isFlatSamplesTable$(workfileId, childTableName ? childTableName : parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.flatTableDataRepository.getOverlappingData$(
							parentTableName.toLowerCase(),
							projectNumber,
							Number(entryId),
							depthFrom,
							depthTo
						),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.dataRepository.getOverlappingData$(
										childTableName.toLowerCase(),
										projectNumber,
										primaryKeyValue,
										Number(childEntryId),
										depthFrom,
										depthTo
									)
								)
							)
					)
				)
			);
	}

	public hasAnyChildTableData$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: string,
		childEntryId: string
	): Observable<boolean> {
		return this.tableTypeService
			.isFlatSamplesTable$(workfileId, childTableName ? childTableName : parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.flatTableDataRepository.hasAnyChildTableData$(
							parentTableName,
							projectNumber,
							Number(entryId)
						),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.dataRepository.hasAnyChildTableData$(
										childTableName,
										projectNumber,
										primaryKeyValue,
										Number(childEntryId)
									)
								)
							)
					)
				)
			);
	}

	public getCombinedSampleData(
		tableName: string,
		standardTableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<[DataModel[], DataModel[]]> {
		return this.tableTypeService
			.isFlatSamplesTable$(this.queryParamsProvider.workfileId, tableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						combineLatest([
							this.dataRepository.getTableData$(tableName, projectNumber),
							this.dataRepository.getTableData$(standardTableName, projectNumber)
						]),
						combineLatest([
							this.dataRepository.getChildTableData$(tableName, projectNumber, primaryKeyValue),
							this.dataRepository.getChildTableData$(standardTableName, projectNumber, primaryKeyValue)
						])
					)
				)
			);
	}

	public getRecordsCount$(
		sourceCollections: string[],
		key: string,
		value: string
	): Observable<CollectionCountAppModel[]> {
		const queries = [];

		for (const collection of sourceCollections) {
			queries.push(
				this.tableTypeService
					.isFlatSamplesTable$(this.queryParamsProvider.workfileId, collection.toUpperCase())
					.pipe(
						switchMap((isFlatSamplesTable: boolean) =>
							iif(
								() => isFlatSamplesTable,
								this.tableRepository.getStandardTable$(collection.toUpperCase()).pipe(
									switchMap((standardTableName: string) =>
										this.dataRepository
											.getRecordsTotalCount$([collection, standardTableName.toLowerCase()], {
												[key]: value
											})
											.pipe(
												map(
													(recordsCount: number) =>
														new CollectionCountAppModel(collection, recordsCount)
												)
											)
									)
								),
								this.dataRepository
									.getRecordsTotalCount$([collection], { [key]: value })
									.pipe(
										map(
											(recordsCount: number) =>
												new CollectionCountAppModel(collection, recordsCount)
										)
									)
							)
						)
					)
			);
		}

		return combineLatest(queries);
	}

	public hasAnyStartsAtZeroData$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: string,
		childEntryId: string
	): Observable<boolean> {
		return this.tableTypeService
			.isFlatSamplesTable$(workfileId, childTableName ? childTableName : parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						this.flatTableDataRepository
							.hasAnyStartsAtZeroData$(parentTableName, projectNumber, Number(entryId))
							.pipe(map((hasAnyStartAtZero: boolean) => hasAnyStartAtZero)),
						this.businessLayerService
							.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, Number(entryId))
							.pipe(
								switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
									this.dataRepository
										.hasAnyStartsAtZeroData$(
											childTableName,
											projectNumber,
											primaryKeyValue,
											Number(childEntryId)
										)
										.pipe(map((hasAnyStartAtZero: boolean) => hasAnyStartAtZero))
								)
							)
					)
				)
			);
	}

	public isDeleteStartAtZeroForbidden$(
		childTableName: string,
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		entryId: number,
		childEntryId: number
	): Observable<boolean> {
		return this.tableTypeService.isFlatSamplesTable$(workfileId, parentTableName).pipe(
			switchMap((isFlatSamplesTable: boolean) =>
				iif(
					() => isFlatSamplesTable,
					combineLatest([
						this.flatTableDataRepository.hasAnyStartsAtZeroData$(
							parentTableName,
							projectNumber,
							childEntryId
						),
						this.flatTableDataRepository.hasAnyChildTableData$(parentTableName, projectNumber, childEntryId)
					]).pipe(
						map(([hasAnyStartAtZero, hasAnyChildData]) => {
							if (!hasAnyStartAtZero && hasAnyChildData) {
								return true;
							}

							return false;
						})
					),
					this.businessLayerService
						.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, entryId)
						.pipe(
							switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
								combineLatest([
									this.dataRepository.hasAnyStartsAtZeroData$(
										childTableName.toLowerCase(),
										projectNumber,
										primaryKeyValue,
										childEntryId
									),
									this.dataRepository.hasAnyChildTableData$(
										childTableName.toLowerCase(),
										projectNumber,
										primaryKeyValue,
										childEntryId
									)
								]).pipe(
									map(([hasAnyStartAtZero, hasAnyChildData]) => {
										if (!hasAnyStartAtZero && hasAnyChildData) {
											return true;
										}

										return false;
									})
								)
							)
						)
				)
			)
		);
	}

	public isSamplesTablePropertyValueUnique$(
		parentTableName: string,
		childTableName: string,
		additionalTableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		propertySampleNumber: string,
		propertyStandardNumber: string,
		propertyValue: string,
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<boolean> {
		return this.tableTypeService.isFlatSamplesTable$(this.queryParamsProvider.workfileId, parentTableName).pipe(
			switchMap((isFlatSamplesTable: boolean) =>
				isFlatSamplesTable
					? this.isFlatSamplesPropertyValueUnique$(
							parentTableName,
							additionalTableName,
							projectNumber,
							primaryKeyValue,
							propertySampleNumber,
							propertyStandardNumber,
							propertyValue,
							excludedId,
							includeSoftDeleted
					  )
					: this.isSamplesPropertyValueUnique$(
							childTableName,
							additionalTableName,
							projectNumber,
							primaryKeyValue,
							propertySampleNumber,
							propertyStandardNumber,
							propertyValue,
							excludedId,
							includeSoftDeleted
					  )
			),
			map(([standardNumberUnique, sampleNumberUnique]) => sampleNumberUnique && standardNumberUnique)
		);
	}

	public isStandardsTablePropertyValueUnique$(
		parentTableName: string,
		childTableName: string,
		additionalTableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		propertySampleNumber: string,
		propertyStandardNumber: string,
		propertyValue: string,
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<boolean> {
		return this.tableTypeService.isFlatSamplesTable$(this.queryParamsProvider.workfileId, parentTableName).pipe(
			switchMap((isFlatSamplesTable: boolean) =>
				iif(
					() => isFlatSamplesTable,
					combineLatest([
						this.flatTableDataRepository.isTablePropertyValueUnique$(
							additionalTableName,
							projectNumber,
							propertySampleNumber,
							propertyValue,
							excludedId,
							includeSoftDeleted
						),
						this.flatTableDataRepository.isTablePropertyValueUnique$(
							childTableName,
							projectNumber,
							propertyStandardNumber,
							propertyValue,
							excludedId,
							includeSoftDeleted
						)
					]),
					combineLatest([
						this.dataRepository.isTablePropertyValueUnique$(
							additionalTableName,
							projectNumber,
							primaryKeyValue,
							propertySampleNumber,
							propertyValue,
							excludedId,
							includeSoftDeleted
						),
						this.dataRepository.isTablePropertyValueUnique$(
							childTableName,
							projectNumber,
							primaryKeyValue,
							propertyStandardNumber,
							propertyValue,
							excludedId,
							includeSoftDeleted
						)
					])
				)
			),
			map(([standardNumberUnique, sampleNumberUnique]) => sampleNumberUnique && standardNumberUnique)
		);
	}

	public getNewSampleNumber$(
		parentTableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<[string | number | null, string | number | null]> {
		return this.tableTypeService
			.isFlatSamplesTable$(this.queryParamsProvider.workfileId, parentTableName)
			.pipe(
				switchMap((isFlatSamplesTable: boolean) =>
					iif(
						() => isFlatSamplesTable,
						combineLatest([
							this.flatTableDataRepository.getLastPropertyValue$(
								this.samplesTableProvider.getSamplesTable,
								WorkspaceDatabaseKeys.columns.sampleNumber,
								projectNumber
							),
							this.flatTableDataRepository.getLastPropertyValue$(
								this.standardTableProvider.standardTable,
								WorkspaceDatabaseKeys.columns.standardNumber,
								projectNumber
							)
						]),
						combineLatest([
							this.dataRepository.getLastPropertyValue$(
								this.samplesTableProvider.getSamplesTable,
								WorkspaceDatabaseKeys.columns.sampleNumber,
								projectNumber,
								primaryKeyValue
							),
							this.dataRepository.getLastPropertyValue$(
								this.standardTableProvider.standardTable,
								WorkspaceDatabaseKeys.columns.standardNumber,
								projectNumber,
								primaryKeyValue
							)
						])
					)
				)
			);
	}

	private isFlatSamplesPropertyValueUnique$(
		parentTableName: string,
		additionalTableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		propertySampleNumber: string,
		propertyStandardNumber: string,
		propertyValue: string,
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<[boolean, boolean]> {
		return combineLatest([
			this.dataRepository.isTablePropertyValueUnique$(
				additionalTableName,
				projectNumber,
				primaryKeyValue,
				propertyStandardNumber,
				propertyValue,
				excludedId,
				includeSoftDeleted
			),
			this.dataRepository.isTablePropertyValueUnique$(
				parentTableName,
				projectNumber,
				primaryKeyValue,
				propertySampleNumber,
				propertyValue,
				excludedId,
				includeSoftDeleted
			)
		]);
	}

	private isSamplesPropertyValueUnique$(
		childTableName: string,
		additionalTableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		propertySampleNumber: string,
		propertyStandardNumber: string,
		propertyValue: string,
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<[boolean, boolean]> {
		return combineLatest([
			this.dataRepository.isTablePropertyValueUnique$(
				additionalTableName,
				projectNumber,
				primaryKeyValue,
				propertyStandardNumber,
				propertyValue,
				excludedId,
				includeSoftDeleted
			),
			this.dataRepository.isTablePropertyValueUnique$(
				childTableName,
				projectNumber,
				primaryKeyValue,
				propertySampleNumber,
				propertyValue,
				excludedId,
				includeSoftDeleted
			)
		]);
	}
}
